import { useQuery } from "@tanstack/react-query";
import { berserkr, isSupabaseConfigured } from "../lib/supabase";

export type ParseJobStatus =
  | "extracting_paragraphs"
  | "completed"
  | "failed";

export interface ParseJob {
  id: string;
  case_id: string;
  filing_id: string;
  status: ParseJobStatus;
  total_paragraphs: number;
  paragraphs_inserted: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

/**
 * Polls a parse_jobs row every 3s while status is in-flight, then stops.
 * Returns the current job state so the UI can render progress / final state.
 */
export function useParseJob(jobId: string | undefined) {
  return useQuery<ParseJob | null, Error>({
    queryKey: ["parse-job", jobId],
    enabled: isSupabaseConfigured() && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;
      if (data.status === "extracting_paragraphs") return 3000;
      return false; // stop polling once completed or failed
    },
    queryFn: async () => {
      if (!jobId) return null;
      const { data, error } = await berserkr()
        .from("parse_jobs")
        .select(
          "id, case_id, filing_id, status, total_paragraphs, paragraphs_inserted, error_message, created_at, updated_at, completed_at",
        )
        .eq("id", jobId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data as ParseJob | null) ?? null;
    },
  });
}

/**
 * Returns the most recent parse_job for a given case, regardless of status.
 * Used by the war room to show an in-flight extraction banner.
 */
export function useLatestParseJobForCase(caseId: string | undefined) {
  return useQuery<ParseJob | null, Error>({
    queryKey: ["latest-parse-job", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === "extracting_paragraphs") return 3000;
      return false;
    },
    queryFn: async () => {
      if (!caseId) return null;
      const { data, error } = await berserkr()
        .from("parse_jobs")
        .select(
          "id, case_id, filing_id, status, total_paragraphs, paragraphs_inserted, error_message, created_at, updated_at, completed_at",
        )
        .eq("case_id", caseId)
        .order("created_at", { ascending: false })
        .limit(1);
      if (error) throw new Error(error.message);
      return ((data?.[0] as ParseJob | undefined) ?? null) as ParseJob | null;
    },
  });
}
